import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, Collapse, IconButton, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { selectFiles, updateFileField } from "../../../Store/userSlice";
import DocumentUploadSection from "../../common/FileUpload/DocumentUploadSection";
import FileUploadField from "../../common/FileUpload/FileUploadField";
import { base64ToFile, convertAndSetFile } from "../../common/Util/Util";
import { assetPath } from "../../../utils/constants";
import {
  entityBankDocumentsValidation,
  entityDocUploadProprietorshipValidation,
} from "../../common/ValidationRules/validationRules";

const BankDetailsDocUploadForm = React.forwardRef((props, ref) => {
  const files = useSelector(selectFiles);
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
    unregister,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      bankStatement: [],
      slLoan: [],
      debitProfile:null,
    },
  });

  const [isBankDetailsExpanded, setIsBankDetailsExpanded] = useState(false);
  const [bankStatements, setBankStatements] = useState([
    { id: 1, isRequired: true },
  ]);
  const [isSlLoanExpanded, setIsSlLoanExpanded] = useState(false);
  const [slLoans, setSlLoans] = useState([{ id: 1, isRequired: true }]);
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleFileSelect = (fieldName) => async (selectedFile) => {
    try {
      if (selectedFile) {
        const base64 = await fileToBase64(selectedFile);
        dispatch(
          updateFileField({
            field: fieldName,
            value: { base64, filename: selectedFile.name },
          })
        );
      }
    } catch (error) {
      console.error("Failed to convert file to base64:", error);
    }
  };

  useEffect(() => {
    if (
      Array.isArray(files.bankStatement) &&
      files.bankStatement.some((file) => file !== null)
    ) {
      setIsBankDetailsExpanded(true);
    }
    if (
      Array.isArray(files.slLoan) &&
      files.slLoan.some((file) => file !== null)
    ) {
      setIsSlLoanExpanded(true);
    }

    if (Array.isArray(files.bankStatement) && files.bankStatement.length > 0) {
      const cleanedBankStatements = [...files.bankStatement];
      while (
        cleanedBankStatements.length > 0 &&
        cleanedBankStatements[cleanedBankStatements.length - 1] === null
      ) {
        cleanedBankStatements.pop();
      }

      if (cleanedBankStatements.length > 0) {
        const newBankStatements = [];
        for (let i = 0; i < cleanedBankStatements.length; i++) {
          newBankStatements.push({ id: i + 1, isRequired: i === 0 });
        }
        if (newBankStatements.length > bankStatements.length) {
          setBankStatements(newBankStatements);
        }
      }
    }

    if (Array.isArray(files.slLoan) && files.slLoan.length > 0) {
      const cleanedSlLoans = [...files.slLoan];
      while (
        cleanedSlLoans.length > 0 &&
        cleanedSlLoans[cleanedSlLoans.length - 1] === null
      ) {
        cleanedSlLoans.pop();
      }

      if (cleanedSlLoans.length > 0) {
        const newSlLoans = [];
        for (let i = 0; i < cleanedSlLoans.length; i++) {
          newSlLoans.push({ id: i + 1, isRequired: i === 0 });
        }
        if (newSlLoans.length > slLoans.length) {
          setSlLoans(newSlLoans);
        }
      }
    }
    if(files.debitProfile){
      setExpanded(true)
    }
  }, [files.bankStatement, files.slLoan]);

  React.useImperativeHandle(ref, () => ({
    validateForm: async () => {
      bankStatements.forEach((bank, index) => {
        const fieldName =
          bank.id === 1 ? "bankStatement" : `bankStatement${bank.id}`;
        const fieldValue =
          index < (files.bankStatement?.length || 0)
            ? files.bankStatement[index]
            : null;

        if (!fieldValue) {
          setValue(fieldName, null);
        }
      });

      slLoans.forEach((loan, index) => {
        const fieldName = loan.id === 1 ? "slLoan" : `slLoan${loan.id}`;
        const fieldValue =
          index < (files.slLoan?.length || 0) ? files.slLoan[index] : null;

        if (!fieldValue) {
          setValue(fieldName, null);
        }
      });

      const result = await trigger();
      return result;
    },
  }));

  const convertAndSetfile = (fieldName, fileData) => {
    if (fileData && fileData.base64 && fileData.filename) {
      const fileObj = base64ToFile(fileData.base64, fileData.filename);
      return fileObj;
    } else if (
      fileData &&
      fileData.remote === true &&
      (fileData.url || fileData.documentPath)
    ) {
      return fileData;
    } else {
      return null;
    }
  };

  useEffect(() => {
    (Array.isArray(files.bankStatement) ? files.bankStatement : []).forEach(
      (file, idx) => {
        setValue(
          idx === 0 ? "bankStatement" : `bankStatement${idx + 1}`,
          file ? base64ToFile(file.base64, file.filename) : null
        );
      }
    );
    (Array.isArray(files.slLoan) ? files.slLoan : []).forEach((file, idx) => {
      setValue(
        idx === 0 ? "slLoan" : `slLoan${idx + 1}`,
        file ? base64ToFile(file.base64, file.filename) : null
      );
    });
    setValue(
      "debitProfile",
      convertAndSetFile(files.debitProfile)
    );
  }, [files]);

  const handleAddBank = () => {
    const newId = bankStatements.length + 1;
    setBankStatements((prev) => [...prev, { id: newId, isRequired: false }]);
  };

  const handleRemoveBank = (id) => {
    if (id === 1) return;
    setBankStatements((prev) => prev.filter((bank) => bank.id !== id));
    unregister(id === 1 ? "bankStatement" : `bankStatement${id}`);

    const bankIndex = id - 1;
    const currentFiles = Array.isArray(files.bankStatement)
      ? [...files.bankStatement]
      : [];
    if (currentFiles.length > bankIndex) {
      currentFiles[bankIndex] = null;

      let lastNonNullIndex = currentFiles.length - 1;
      while (lastNonNullIndex >= 0 && currentFiles[lastNonNullIndex] === null) {
        lastNonNullIndex--;
      }

      const cleanedFiles = currentFiles.slice(0, lastNonNullIndex + 1);

      dispatch(
        updateFileField({
          field: "bankStatement",
          value: cleanedFiles,
        })
      );
    }
  };

  const handleBankFileChange = (idx) => async (file) => {
    try {
      const currentFiles = Array.isArray(files.bankStatement)
        ? [...files.bankStatement]
        : [];

      if (!file) {
        if (currentFiles.length > idx) {
          currentFiles[idx] = null;
          dispatch(
            updateFileField({
              field: "bankStatement",
              value: currentFiles,
            })
          );
        }
        return;
      }

      // Convert file to base64
      const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
        });
      const base64 = await fileToBase64(file);
      const fileObj = { base64, filename: file.name };

      while (currentFiles.length <= idx) {
        currentFiles.push(null);
      }
      currentFiles[idx] = fileObj;

      dispatch(
        updateFileField({
          field: "bankStatement",
          value: currentFiles,
        })
      );
    } catch (error) {
      console.error("Error handling bank file change:", error);
    }
  };

  const handleAddSlLoan = () => {
    const newId = slLoans.length + 1;
    setSlLoans((prev) => [...prev, { id: newId, isRequired: false }]);
  };

  const handleRemoveSlLoan = (id) => {
    if (id === 1) return;
    setSlLoans((prev) => prev.filter((loan) => loan.id !== id));
    unregister(id === 1 ? "slLoan" : `slLoan${id}`);
    const loanIndex = id - 1;
    const currentFiles = Array.isArray(files.slLoan) ? [...files.slLoan] : [];
    if (currentFiles.length > loanIndex) {
      currentFiles[loanIndex] = null;

      let lastNonNullIndex = currentFiles.length - 1;
      while (lastNonNullIndex >= 0 && currentFiles[lastNonNullIndex] === null) {
        lastNonNullIndex--;
      }

      const cleanedFiles = currentFiles.slice(0, lastNonNullIndex + 1);

      dispatch(
        updateFileField({
          field: "slLoan",
          value: cleanedFiles,
        })
      );
    }
  };

  const handleSlLoanFileChange = (idx) => async (file) => {
    try {
      const currentFiles = Array.isArray(files.slLoan) ? [...files.slLoan] : [];

      if (!file) {
        if (currentFiles.length > idx) {
          currentFiles[idx] = null;
          dispatch(
            updateFileField({
              field: "slLoan",
              value: currentFiles,
            })
          );
        }
        return;
      }

      const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
        });
      const base64 = await fileToBase64(file);
      const fileObj = { base64, filename: file.name };

      while (currentFiles.length <= idx) {
        currentFiles.push(null);
      }
      currentFiles[idx] = fileObj;

      dispatch(
        updateFileField({
          field: "slLoan",
          value: currentFiles,
        })
      );
    } catch (error) {
      console.error("Error handling SL loan file change:", error);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Step 4/5
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: "flex-end",
        }}
      >
        <Typography sx={{ color: "#666" }}>
          Upload files in JPEG, PNG, or PDF format, up to 5 MB in size.
        </Typography>
        <InfoOutlinedIcon sx={{ ml: 1, color: "#666", fontSize: 20 }} />
      </Box>

      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          onClick={() => setIsBankDetailsExpanded(!isBankDetailsExpanded)}
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            backgroundColor: "#f5f5f5",
            "&:hover": { backgroundColor: "#eeeeee" },
          }}
        >
          <Typography variant="h6">Upload Bank Statements(Optional)</Typography>
          {isBankDetailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>

        <Collapse in={isBankDetailsExpanded}>
          <Box sx={{ p: 3 }}>
            {bankStatements.map((bank, index) => (
              <Box key={bank.id} sx={{ mb: 2, position: "relative" }}>
                <DocumentUploadSection
                  control={control}
                  title={`Last 12 Month Statement Bank - ${bank.id} (Optional)`}
                  name={
                    bank.id === 1 ? "bankStatement" : `bankStatement${bank.id}`
                  }
                  setValue={setValue}
                  validationRule={
                    entityBankDocumentsValidation.bankStatementOptional
                  }
                  errors={
                    errors?.[
                      bank.id === 1
                        ? "bankStatement"
                        : `bankStatement${bank.id}`
                    ]
                  }
                  helptext={
                    errors?.[
                      bank.id === 1
                        ? "bankStatement"
                        : `bankStatement${bank.id}`
                    ]?.message
                  }
                  value={convertAndSetfile(
                    bank.id === 1 ? "bankStatement" : `bankStatement${bank.id}`,
                    files?.bankStatement?.[bank.id - 1] || null
                  )}
                  onFileSelect={handleBankFileChange(bank.id - 1)}
                />
                {!bank.isRequired && (
                  <Button
                    onClick={() => handleRemoveBank(bank.id)}
                    color="error"
                    variant="text"
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 16, zIndex: 2 }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddBank}
              variant="outlined"
              sx={{ alignSelf: "flex-start" }}
            >
              Add more Bank Statement
            </Button>
          </Box>
        </Collapse>
      </Box>
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => setIsSlLoanExpanded(!isSlLoanExpanded)}
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            backgroundColor: "#f5f5f5",
            "&:hover": { backgroundColor: "#eeeeee" },
          }}
        >
          <Typography variant="h6">
            Upload Sanction Letters (Optional)
          </Typography>
          {isSlLoanExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Collapse in={isSlLoanExpanded}>
          <Box sx={{ p: 3 }}>
            {slLoans.map((loan, idx) => (
              <Box key={loan.id} sx={{ mb: 2, position: "relative" }}>
                <DocumentUploadSection
                  control={control}
                  title={`Sanction Letter ${loan.id} (Optional)`}
                  name={loan.id === 1 ? "slLoan" : `slLoan${loan.id}`}
                  setValue={setValue}
                  validationRule={
                    entityBankDocumentsValidation.bankStatementOptional
                  }
                  errors={
                    errors?.[loan.id === 1 ? "slLoan" : `slLoan${loan.id}`]
                  }
                  helptext={
                    errors?.[loan.id === 1 ? "slLoan" : `slLoan${loan.id}`]
                      ?.message
                  }
                  value={convertAndSetfile(
                    loan.id === 1 ? "slLoan" : `slLoan${loan.id}`,
                    files?.slLoan?.[loan.id - 1] || null
                  )}
                  onFileSelect={handleSlLoanFileChange(loan.id - 1)}
                />
                {!loan.isRequired && (
                  <Button
                    onClick={() => handleRemoveSlLoan(loan.id)}
                    color="error"
                    variant="text"
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 16, zIndex: 2 }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddSlLoan}
              variant="outlined"
              sx={{ alignSelf: "flex-start" }}
            >
              {`Add SL ${slLoans.length >= 1 ? slLoans.length + 1 : ""}`}
            </Button>
          </Box>
        </Collapse>
      </Box>

      <Box>
        {" "}
        {/* Proprietorship doc fields here */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            backgroundColor: "#f5f5f5",
            "&:hover": { backgroundColor: "#eeeeee" },
          }}
          onClick={() => {
            if (expanded) {
              setExpanded(false);
            } else {
              setExpanded(true);
            }
          }}
        >
          <Typography variant="h6">Upload Debt Profile</Typography>
          <IconButton>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={expanded}>
          <Box
            sx={{
              p: 3,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "white",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                color: "#666",
              }}
            >
              <Typography variant="body2">
                Please follow the template format for your debt profile file.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                component="a"
                href={assetPath("DEBT PROFILE.XLSX")}
                download
              >
                Download Excel Template
              </Button>
            </Box>
            <Controller
              name="debitProfile"
              control={control}
              rules={entityDocUploadProprietorshipValidation?.legalDocuments}
              render={({ field }) => (
                <FileUploadField
                  name="debitProfile"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Upload Debt Profile
                    </Box>
                  }
                  error={!!errors.debitProfile}
                  helperText={errors.debitProfile?.message}
                  value={convertAndSetFile(files?.debitProfile)}
                  onFileSelect={(file) => {
                    field.onChange(file);
                    handleFileSelect("debitProfile")(file);
                  }}
                />
              )}
            />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
});

export default BankDetailsDocUploadForm;
